import MatchingTransactionsTable from '@/components/cashflow/rules/modal/MatchingTransactionsTable';
import RuleForm from '@/components/cashflow/rules/modal/RuleForm';
import Modal from '@/components/common/modals/Modal';
import { useErrorToast } from '@/components/common/Toasts';
import { FinancialEntitiesProvider } from '@/components/financialEntities/FinancialEntitiesProvider';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import {
    RuleContext,
    RuleViewProps,
    useBuildRuleContext,
} from '@common/react/cashflow/rules';
import { useFormState } from '@common/react/cashflow/rules/useFormState';

export const showRuleModal = (props: RuleViewProps) =>
    ModalManager.show(RuleModal, props);

const RuleModal = ModalManager.create<RuleViewProps>(
    ({
        rule,
        defaultConditions,
        defaultChanges,
        slideDown = false,
        isGlobalRule,
    }) => {
        useTrackModalShown('Transaction Entry Rule');
        const modal = useModal();

        return (
            <Modal
                isOpen={modal.visible}
                onClose={modal.remove}
                closeOnOverlayClick={false}
                size="4xl"
                scrollBehavior="outside"
                slideDown={slideDown}
            >
                <InnerRuleModal
                    rule={rule}
                    defaultConditions={defaultConditions}
                    defaultChanges={defaultChanges}
                    isGlobalRule={isGlobalRule}
                />
            </Modal>
        );
    }
);

const InnerRuleModal = ({
    rule,
    defaultConditions,
    defaultChanges,
    isGlobalRule,
}: RuleViewProps) => {
    const showErrorToast = useErrorToast();

    const {
        conditions,
        changes,
        isSubmitting,
        setIsSubmitting,
        handleConditionChange,
        handleChangeChange,
        applyToAll,
        setApplyToAll,
    } = useFormState({ rule, defaultConditions, defaultChanges, isGlobalRule });

    const {
        view,
        setView,
        enabledValidConditions,
        enabledValidChanges,
        isFormValid,
        matchingTransactions,
        matchingTransactionsLoading,
        financialEntities,
    } = useBuildRuleContext({ conditions, changes, showErrorToast });

    return (
        <FinancialEntitiesProvider>
            <RuleContext.Provider
                value={{
                    conditions,
                    changes,
                    enabledValidChanges,
                    enabledValidConditions,
                    isSubmitting,
                    setIsSubmitting,
                    handleConditionChange,
                    handleChangeChange,
                    applyToAll,
                    setApplyToAll,
                    matchingTransactions,
                    matchingTransactionsLoading,
                    setView,
                    isFormValid,
                    rule,
                    isGlobalRule,
                    financialEntities,
                }}
            >
                {view === 'form' && <RuleForm />}
                {view === 'transactions' && <MatchingTransactionsTable />}
            </RuleContext.Provider>
        </FinancialEntitiesProvider>
    );
};
